import { Navigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { isOmegaAdmin } from '@/features/omega/adminMode';
import { Breadcrumbs } from '@/features/omega/Breadcrumbs';
import { apiFetch } from '@/api/http';

// ── Types ────────────────────────────────────────────────────────────────────

interface PeriodCloseResponse {
  close_id: string;
  period_type: string;
  period: string;
  status: string;
  closed_at: string | null;
  closed_by: string | null;
}

// ── Constants ─────────────────────────────────────────────────────────────────

export const PERIOD_TYPE_LABELS: Record<string, string> = {
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  annual: 'Annual',
};

// ── Helpers ──────────────────────────────────────────────────────────────────

export function fmtDate(iso: string): string {
  return new Date(iso).toLocaleString('he-IL', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export function closeStatusLabel(s: string): string {
  switch (s) {
    case 'open': return 'Open';
    case 'closing': return 'Closing';
    case 'closed': return 'Closed';
    case 'approved': return 'Approved';
    default: return s;
  }
}

function statusColor(s: string): string {
  switch (s) {
    case 'open': return '#fbbf24';
    case 'closing': return '#a78bfa';
    case 'closed': return '#7dd3fc';
    case 'approved': return '#34d399';
    default: return 'var(--omega-ink-subtle)';
  }
}

// ── Page ─────────────────────────────────────────────────────────────────────

export function FinancePeriodClosesPage() {
  if (!isOmegaAdmin()) return <Navigate to="/" replace />;

  const { data, isPending, isError } = useQuery({
    queryKey: ['finance', 'period-closes'],
    queryFn: () => apiFetch<PeriodCloseResponse[]>('/finance/period-closes'),
    refetchInterval: 60_000,
  });

  const closes = data ?? [];

  return (
    <div className="omega-shell" style={{ fontFamily: 'var(--omega-sans)', color: 'var(--omega-ink)', padding: '16px 20px' }}>
      <Breadcrumbs crumbs={[
        { label: 'OMEGA', to: '/omega' },
        { label: 'Finance', to: '/omega/finance' },
        { label: 'Period closings' },
      ]} />

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14, marginTop: 8 }}>
        <h1 style={{ margin: 0, fontSize: '11pt', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
          Period closings
        </h1>
        {data && (
          <span style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 3, fontSize: '8pt', padding: '1px 7px', color: 'var(--omega-ink-subtle)' }}>
            {closes.length}
          </span>
        )}
      </div>

      {isPending && <div style={{ color: 'var(--omega-ink-subtle)', fontSize: '9pt' }}>Loading…</div>}
      {isError && <div style={{ color: '#f87171', fontSize: '9pt' }}>Error loading data.</div>}

      {closes.length > 0 && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: '100px 100px 120px 160px 1fr',
          gap: 10,
          padding: '6px 12px',
          borderBottom: '1px solid rgba(255,255,255,0.1)',
          marginBottom: 2,
        }}>
          {['Type', 'Period', 'Status', 'Closed', 'Closed by'].map(h => (
            <span key={h} style={{ fontSize: '8.5pt', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--omega-ink-subtle)' }}>
              {h}
            </span>
          ))}
        </div>
      )}

      {!isPending && !isError && closes.length === 0 && (
        <div style={{ color: 'var(--omega-ink-subtle)', fontSize: '9pt', fontStyle: 'italic', padding: '40px 0', textAlign: 'center' }}>
          No period closings.
        </div>
      )}

      {closes.map(c => (
        <div
          key={c.close_id}
          style={{
            display: 'grid',
            gridTemplateColumns: '100px 100px 120px 160px 1fr',
            gap: 10,
            alignItems: 'center',
            padding: '10px 12px',
            borderTop: '1px solid rgba(255,255,255,0.06)',
          }}
        >
          <span style={{ fontSize: '8.5pt', color: 'var(--omega-ink-subtle)', fontFamily: 'var(--omega-mono)' }}>
            {PERIOD_TYPE_LABELS[c.period_type] ?? c.period_type}
          </span>
          <span style={{ fontSize: '9pt', fontFamily: 'var(--omega-mono)', color: 'var(--omega-ink)' }}>
            {c.period}
          </span>
          <span style={{
            fontSize: '7.5pt', padding: '1px 6px',
            border: `1px solid ${statusColor(c.status)}`,
            color: statusColor(c.status),
            letterSpacing: '0.06em', fontWeight: 700, borderRadius: 3,
            justifySelf: 'start', whiteSpace: 'nowrap',
          }}>
            {closeStatusLabel(c.status)}
          </span>
          <span style={{ fontSize: '8.5pt', color: 'var(--omega-ink-subtle)', fontFamily: 'var(--omega-mono)' }}>
            {c.closed_at ? fmtDate(c.closed_at) : '—'}
          </span>
          <span style={{ fontSize: '8.5pt', color: 'var(--omega-ink-subtle)', fontFamily: 'var(--omega-mono)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {c.closed_by ?? '—'}
          </span>
        </div>
      ))}
    </div>
  );
}
